import React, { useState, useRef } from 'react';
import {
  MDBRow,
  MDBCol,
  MDBBtn,
  MDBIcon,
  MDBTypography,
  MDBCard,
  MDBCardBody,
  MDBSpinner,
  MDBTable,
  MDBTableHead,
  MDBTableBody
} from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';
import { uploadData, getUrl, remove } from 'aws-amplify/storage';
import { captureError } from '../../../utils/sentryHelpers';

const DocumentsTab = ({
  event,
  onInputChange
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const documents = event?.documents || [];

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // Filter only PDF files
    const pdfFiles = files.filter(file => file.name.toLowerCase().endsWith('.pdf'));
    const nonPdfCount = files.length - pdfFiles.length;

    if (nonPdfCount > 0) {
      setUploadError(t('{{count}} non-PDF file(s) were skipped. Only PDF files are allowed.', { count: nonPdfCount }));
    }

    if (pdfFiles.length === 0) {
      if (nonPdfCount === 0) return;
      return; // All files were non-PDF
    }

    setIsUploading(true);
    if (nonPdfCount === 0) setUploadError(null);

    try {
      const newDocuments = [];

      for (const file of pdfFiles) {
        const fileName = `${Date.now()}-${file.name}`;
        const s3Path = `public/eventDocuments/${fileName}`;

        await uploadData({
          key: `eventDocuments/${fileName}`,
          data: file,
          options: {
            accessLevel: 'guest' // public access
          }
        }).result;

        newDocuments.push({
          fileName: file.name,
          s3Path: s3Path
        });
      }

      const updatedDocuments = [...documents, ...newDocuments];
      onInputChange('documents', updatedDocuments);

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      captureError(error, {
        feature: 'event-management-admin',
        operation: 'upload-event-document',
        consoleMessage: 'Error uploading document',
        context: { fileCount: pdfFiles.length }
      });
      setUploadError(t('Error uploading files. Please try again.'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveDocument = async (index) => {
    const document = documents[index];

    try {
      // Remove from S3
      const key = document.s3Path.replace('public/', '');
      await remove({ key });
    } catch (error) {
      console.error('Error removing file from S3:', error);
      captureError(error, {
        feature: 'event-management-admin',
        operation: 'remove-event-document',
        consoleMessage: 'Error removing file from S3',
        context: { s3Path: document.s3Path }
      });
      // Continue anyway to remove from list
    }

    const updatedDocuments = documents.filter((_, i) => i !== index);
    onInputChange('documents', updatedDocuments);
  };

  const handleDownloadDocument = async (document) => {
    try {
      const key = document.s3Path.replace('public/', '');
      const urlResult = await getUrl({ key });
      window.open(urlResult.url, '_blank');
    } catch (error) {
      console.error('Error downloading document:', error);
      captureError(error, {
        feature: 'event-management-admin',
        operation: 'download-event-document',
        consoleMessage: 'Error downloading document',
        context: { s3Path: document.s3Path }
      });
    }
  };

  return (
    <>
      <MDBTypography tag="h5" className="mb-3">{t('Event Documents')}</MDBTypography>
      <MDBTypography tag="p" className="text-muted mb-4">
        {t('Upload PDF documents that will be available to exhibitors on their booth management page.')}
      </MDBTypography>

      {/* Upload Section */}
      <MDBRow className="mb-4">
        <MDBCol md="6">
          <MDBCard
            className="text-center p-4"
            style={{
              borderStyle: 'dashed',
              borderWidth: '2px',
              borderColor: '#dee2e6',
              cursor: isUploading ? 'not-allowed' : 'pointer'
            }}
            onClick={() => !isUploading && fileInputRef.current?.click()}
          >
            <MDBCardBody>
              {isUploading ? (
                <>
                  <MDBSpinner size="sm" className="mb-2" />
                  <MDBTypography tag="p" className="mb-0">
                    {t('Uploading...')}
                  </MDBTypography>
                </>
              ) : (
                <>
                  <MDBIcon fas icon="file-pdf" size="2x" className="text-muted mb-2" />
                  <MDBTypography tag="p" className="mb-1">
                    {t('Upload PDFs')}
                  </MDBTypography>
                  <MDBTypography tag="small" className="text-muted">
                    {t('Click to select files')}
                  </MDBTypography>
                </>
              )}
            </MDBCardBody>
          </MDBCard>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          {uploadError && (
            <div className="alert alert-danger mt-3" role="alert">
              <MDBIcon fas icon="exclamation-triangle" className="me-2" />
              {uploadError}
            </div>
          )}
        </MDBCol>

        {/* Documents List */}
        <MDBCol md="6">
          {documents.length > 0 ? (
            <MDBTable>
              <MDBTableHead>
                <tr>
                  <th>{t('Document')}</th>
                  <th style={{ width: '120px' }}>{t('Actions')}</th>
                </tr>
              </MDBTableHead>
              <MDBTableBody>
                {documents.map((document, index) => (
                  <tr key={index}>
                    <td>
                      <MDBIcon fas icon="file-pdf" className="text-danger me-2" />
                      {document.fileName}
                    </td>
                    <td>
                      <MDBBtn
                        color="link"
                        size="sm"
                        onClick={() => handleDownloadDocument(document)}
                        className="p-1 me-2"
                        title={t('Download')}
                      >
                        <MDBIcon fas icon="download" />
                      </MDBBtn>
                      <MDBBtn
                        color="link"
                        size="sm"
                        onClick={() => handleRemoveDocument(index)}
                        className="p-1 text-danger"
                        title={t('Remove')}
                      >
                        <MDBIcon fas icon="trash" />
                      </MDBBtn>
                    </td>
                  </tr>
                ))}
              </MDBTableBody>
            </MDBTable>
          ) : (
            <MDBTypography tag="p" className="text-muted text-center">
              {t('No documents uploaded yet.')}
            </MDBTypography>
          )}
        </MDBCol>
      </MDBRow>
    </>
  );
};

export default DocumentsTab;